import {
    NinchatAudienceMetadata,
    NinchatChannelMembersMetadata,
    NinchatQueueRequest,
} from 'api/types/properties'
import {NinchatEmbedConfig} from 'embed/types'
import {QueueRequest} from 'embed/types/events'

const matchesWildcardDomain = (targetOrigin: string, pattern: string): boolean => {
    // Exact match
    if (pattern === targetOrigin) {
        return true
    }

    // Wildcard matching
    if (pattern.includes('*')) {
        // Escape special regex characters except *
        const regexPattern = pattern
            .replace(/[.+?^${}()|[\]\\]/gu, '\\$&')
            .replace(/\*/gu, '.*')

        const regex = new RegExp(`^${regexPattern}$`, 'u')
        return regex.test(targetOrigin)
    }

    return false
}

export const isOriginAllowed = (targetOrigin: string, allowedParents: string[]): boolean => allowedParents.some((pattern) => matchesWildcardDomain(targetOrigin, pattern))

export const composeMetadata = (membersMetadata: NinchatChannelMembersMetadata = {}, targetOrigin: NinchatEmbedConfig['targetOrigin']) => {
    const sharedMetadata = []

    for (const [, metadata] of Object.entries(membersMetadata)) {
        if (metadata.secure?.shared && isOriginAllowed(targetOrigin, metadata.secure.shared.parent)) {
            sharedMetadata.push(metadata.secure.shared.data)
        }
    }

    // Shuffle metadata array to prevent invalid assumptions of the order
    return sharedMetadata.slice().sort(() => 0.5 - Math.random())
}

export const composeMetadataFromAudienceMetadata = (
    audienceMetadata: NinchatAudienceMetadata | undefined,
    targetOrigin: NinchatEmbedConfig['targetOrigin'],
): Record<string, unknown>[] => {
    if (!audienceMetadata?.secure?.shared) {
        return []
    }

    if (!isOriginAllowed(targetOrigin, audienceMetadata.secure.shared.parent)) {
        return []
    }

    return [audienceMetadata.secure.shared.data]
}

export const composeQueueRequest = (
    queueRequest: NinchatQueueRequest,
    targetOrigin: NinchatEmbedConfig['targetOrigin'],
): QueueRequest => {
    const metadata = composeMetadataFromAudienceMetadata(queueRequest.audience_metadata ?? undefined, targetOrigin)

    return {
        audience_id: queueRequest.audience_id,
        ...metadata.length && {metadata},
        request_time: queueRequest.request_time,
    }
}

export const composeQueueRequests = (
    queueRequests: NinchatQueueRequest[],
    targetOrigin: NinchatEmbedConfig['targetOrigin'],
): QueueRequest[] => queueRequests.map((queueRequest) => composeQueueRequest(queueRequest, targetOrigin))
